import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

const key = ["/api/admin/gpt-image-version"];
export function GptImageVersionSetting() {
  const client = useQueryClient();
  const { toast } = useToast();
  const [choice, setChoice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const query = useQuery<{ value: string; editor: string; updatedAt: string | null }>({
    queryKey: key, queryFn: async () => (await apiRequest("GET", key[0])).json(),
    refetchInterval: 15000,
  });
  const save = useMutation({
    mutationFn: async () => (await apiRequest("PUT", key[0], { value: choice })).json(),
    onSuccess: () => {
      setChoice(null);
      void client.invalidateQueries({ queryKey: key });
      toast({ title: "Global GPT Image version saved" });
    },
    onError: () => toast({ title: "Could not save GPT Image version", variant: "destructive" }),
  });
  return <Card>
    <CardHeader><CardTitle>GPT-Image version (global)</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <p className="text-sm text-muted-foreground">Applies to eligible automatic Fal GPT Image calls app-wide.
        Explicit engine choices, Concept Boards and KIE paths are unchanged. Takes effect within 30 seconds.</p>
      {query.isLoading ? <p>Loading current version...</p> : query.isError ?
        <div role="alert">Could not load the current version. <Button onClick={() => void query.refetch()}>Retry</Button></div> :
        <>
          <p>Current version: {query.data?.value}</p>
          <p className="text-sm text-muted-foreground">Last editor: {query.data?.editor}<br />
            Last change: {query.data?.updatedAt ? new Date(query.data.updatedAt).toLocaleString() : "Not recorded"}</p>
          <Select value={choice ?? query.data?.value} onValueChange={setChoice}>
            <SelectTrigger aria-label="GPT-Image version (global)" data-testid="select-global-gpt-image-version"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="2">2</SelectItem><SelectItem value="2.5">2.5</SelectItem></SelectContent>
          </Select>
          <Button disabled={!choice || choice === query.data?.value || save.isPending}
            onClick={() => setConfirm(true)} data-testid="save-global-gpt-image-version">Save global version</Button>
        </>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Change global GPT Image version?</AlertDialogTitle>
          <AlertDialogDescription>Changing to {choice} affects every eligible automatic GPT Image call app-wide.
            Already submitted jobs keep their original endpoint. Explicit engine choices and Concept Boards are unchanged.</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => save.mutate()}>Confirm change</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </CardContent>
  </Card>;
}
